import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell, CheckCheck, Archive, Trash2, AtSign, Info, Zap, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { SpaceAvatar } from "@/components/Avatars";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const TYPE_ICONS = { action: Zap, info: Info, reminder: Clock, mention: AtSign };
const TYPE_COLORS = { action: "#F9AB00", info: "#1A73E8", reminder: "#9D4CDD", mention: "#D93025" };
const FILTERS = [["pending", "Pendientes"], ["read", "Leídas"], ["archived", "Archivadas"]];

export default function InboxPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("pending");

  const { data: notifications = [] } = useQuery({
    queryKey: ["notifications", filter],
    queryFn: async () => (await api.get(`/notifications?filter=${filter}`)).data,
    refetchInterval: 10000,
  });
  const { data: home } = useQuery({ queryKey: ["home"], queryFn: async () => (await api.get("/home")).data });
  const invitations = home?.pending_invitations || [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["notifications"] });
    qc.invalidateQueries({ queryKey: ["notif-count"] });
  };

  const open = async (n) => {
    await api.put(`/notifications/${n.id}`, { read: true });
    invalidate();
    if (n.entity_type === "conversation") {
      navigate(`/space/${n.workspace_id}/chat?c=${n.entity_id}`);
    } else if (n.entity_type === "event") {
      navigate("/calendar");
    } else if (n.entity_type === "poll") {
      navigate(`/space/${n.workspace_id}/polls`);
    } else if (n.entity_type === "task") {
      try {
        const { data: task } = await api.get(`/tasks/${n.entity_id}`);
        navigate(`/space/${n.workspace_id}/project/${task.project_id}?task=${task.id}`);
      } catch {
        navigate(`/space/${n.workspace_id}`);
      }
    } else {
      navigate(`/space/${n.workspace_id}`);
    }
  };

  const act = async (fn) => {
    try {
      await fn();
      invalidate();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const acceptInvitation = async (token) => {
    try {
      const { data } = await api.post(`/invitations/${token}/accept`);
      qc.invalidateQueries({ queryKey: ["home"] });
      toast.success("Te has unido al Espacio");
      navigate(`/space/${data.workspace_id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 space-y-5" data-testid="inbox-page">
      <div className="flex items-center gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex-1">Bandeja de entrada</h1>
        <Button variant="outline" size="sm" data-testid="inbox-read-all-btn"
          onClick={() => act(() => api.post("/notifications/read-all"))}>
          <CheckCheck className="h-4 w-4 mr-1" /> Marcar todas
        </Button>
      </div>

      {invitations.length > 0 && (
        <div className="space-y-2">
          {invitations.map((inv) => (
            <Card key={inv.token} className="p-4 flex items-center gap-3" data-testid={`inbox-invitation-${inv.token}`}>
              <SpaceAvatar icon={inv.workspace_icon} color={inv.workspace_color} size="md" />
              <p className="text-sm flex-1 min-w-0">
                <b>{inv.invited_by_name}</b> te ha invitado a <b>{inv.workspace_name}</b>
              </p>
              <Button size="sm" data-testid={`inbox-accept-invitation-${inv.token}`} onClick={() => acceptInvitation(inv.token)}>
                Unirme
              </Button>
            </Card>
          ))}
        </div>
      )}

      <div className="flex gap-1.5">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`inbox-filter-${v}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === v ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}>
            {l}
          </button>
        ))}
      </div>

      {notifications.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Bell className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Nada por aquí. Te avisaremos solo cuando importe.</p>
        </Card>
      ) : (
        <Card className="divide-y divide-border">
          {notifications.map((n) => {
            const Icon = TYPE_ICONS[n.type] || Info;
            return (
              <div key={n.id} className={`p-3.5 flex items-center gap-3 group ${!n.read ? "bg-accent/30" : ""}`}
                data-testid={`notification-row-${n.id}`}>
                <span className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${TYPE_COLORS[n.type]}1a`, color: TYPE_COLORS[n.type] }}>
                  <Icon className="h-4 w-4" />
                </span>
                <button className="flex-1 min-w-0 text-left" onClick={() => open(n)} data-testid={`notification-open-${n.id}`}>
                  <p className="text-sm font-medium truncate">{n.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {n.workspace_icon} {n.workspace_name} · {n.body || timeAgo(n.created_at)}
                  </p>
                </button>
                <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(n.created_at)}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  {!n.archived && (
                    <button className="p-1 text-muted-foreground hover:text-foreground" data-testid={`notification-archive-${n.id}`}
                      onClick={() => act(() => api.put(`/notifications/${n.id}`, { archived: true, read: true }))}>
                      <Archive className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button className="p-1 text-destructive" data-testid={`notification-delete-${n.id}`}
                    onClick={() => act(() => api.delete(`/notifications/${n.id}`))}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
